import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store';
import { translations } from '../translations';
import { useFormik } from 'formik';
import * as yup from 'yup';

const validationSchema = yup.object({
  email: yup.string('Enter email').email('Invalid email').required('Email is required'),
  password: yup.string('Enter password').required('Password is required')
});

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, loading, language } = useAuthStore();
  const t = translations[language];
  const [error, setError] = React.useState(null);

  const formik = useFormik({
    initialValues: {
      email: '',
      password: ''
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        await login(values.email, values.password);
        navigate('/dashboard');
      } catch (err) {
        setError(err.response?.data?.message || 'Login failed');
      }
    }
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-600 via-blue-700 to-blue-900 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-cyan-400/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-400/20 rounded-full blur-3xl"></div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 w-full max-w-md relative z-10">
        <div className="text-center mb-8">
          <img 
              src="/caldea-logo.png" 
              alt="Caldea" 
              className="h-12 object-contain mx-auto mb-2"
              onError={(e) => { e.target.style.display = 'none'; }}
          />
          <p className="text-cyan-600 dark:text-cyan-400 text-xs font-semibold">{t.poweredBy}</p>
          <p className="text-gray-400 dark:text-gray-500 text-xs mt-2">{t.loginTitle}</p>
        </div>

        {error && (
          <div className="bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        <form onSubmit={formik.handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-700 dark:text-gray-200 font-semibold mb-2">{t.emailLabel}</label>
            <input
              type="email"
              name="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 dark:focus:ring-cyan-800 transition"
              placeholder={t.emailPlaceholder}
            />
            {formik.touched.email && formik.errors.email && (
              <p className="text-red-600 dark:text-red-400 text-sm mt-1">{formik.errors.email}</p>
            )}
          </div>

          <div className="mb-6">
            <label className="block text-gray-700 dark:text-gray-200 font-semibold mb-2">{t.passwordLabel}</label>
            <input
              type="password"
              name="password"
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 dark:focus:ring-cyan-800 transition"
              placeholder={t.passwordPlaceholder}
            />
            {formik.touched.password && formik.errors.password && (
              <p className="text-red-600 dark:text-red-400 text-sm mt-1">{formik.errors.password}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white font-semibold py-3 rounded-xl transition disabled:opacity-50 shadow-lg"
          >
            {loading ? t.loginButtonLoading : t.loginButton}
          </button>
        </form>

        <div className="mt-6 p-4 bg-cyan-50 dark:bg-cyan-900/20 rounded-xl">
          <p className="text-center text-cyan-700 dark:text-cyan-300 text-sm font-medium mb-2">
            {t.welcomeReward}
          </p>
          <p className="text-center text-cyan-600 dark:text-cyan-400 text-xs">
            {t.accumulatePoints}
          </p>
        </div>
      </div>
    </div>
  );
}
